/** @format */

import axios from "axios";
import {
	clearBusinessSession,
	hasBusinessSession,
} from "../redux/services/businessAuthService";
import { IMPERSONATION_STORAGE_KEY } from "../components/collect/admin/impersonationStorage";

/**
 * Module-level guard so a burst of simultaneous 401s (the app fires several
 * Collect requests on boot) triggers the forced sign-out + redirect exactly
 * once instead of racing multiple full-page navigations.
 */
let businessSessionExpiredHandled = false;

/**
 * The response interceptor below swallows 401s and resolves with this sentinel
 * instead of rejecting. Callers that need to react to an expired session must
 * detect it explicitly, otherwise they see a "successful" response with no data.
 */
export const AUTH_REQUIRED_ERROR = "Authentication required";

export const isAuthRequiredResponse = (data: any) =>
	data?.error === AUTH_REQUIRED_ERROR && data?.valid === false;

/**
 * Global axios interceptors to handle authentication and account status errors
 *
 * IMPORTANT: Mobile Safari (iOS) blocks third-party cookies by default due to
 * Intelligent Tracking Prevention (ITP). Since our API is on a different domain
 * than the frontend, cookies are considered "third-party" and may not be sent.
 *
 * To fix this, we send the token via Authorization header as a fallback.
 * The backend accepts tokens from both cookies and the Authorization header.
 */
export const setupAxiosInterceptors = (navigate: any) => {
	// Request interceptor - Add Authorization header for mobile browser support
	axios.interceptors.request.use(
		(config) => {
			// ── Collect business session ──────────────────────────────────
			// The Collect product authenticates a *BusinessUser* (separate
			// Cognito session) using business-scoped tokens stored under
			// `b_*` localStorage keys. For any request targeting a Collect
			// endpoint, prefer the business session when one exists so the
			// backend's unified `checkCollectAuth` resolves the business
			// principal. We mark these requests with `x-business-auth` so the
			// backend never confuses them with a circle session.
			const url = (config.url || "").toString();
			const isCollectRequest = url.includes("/collect/");
			const bAccessToken = localStorage.getItem("b_accessToken");
			const bIdToken = localStorage.getItem("b_idToken");
			const bRefreshToken = localStorage.getItem("b_refreshToken");

			if (isCollectRequest && bAccessToken && config.headers) {
				config.headers["Authorization"] = `Bearer ${bAccessToken}`;
				config.headers["X-Business-Auth"] = "1";
				if (bIdToken) config.headers["X-ID-Token"] = bIdToken;
				if (bRefreshToken) config.headers["X-Refresh-Token"] = bRefreshToken;
				return config;
			}

			// Get tokens from localStorage (set during login)
			const idToken = localStorage.getItem("idToken");
			const accessToken = localStorage.getItem("accessToken");
			const refreshToken = localStorage.getItem("refreshToken");

			// Use accessToken if available, fallback to idToken
			const token = accessToken || idToken;

			if (token && config.headers) {
				// Send token as Bearer token in Authorization header
				// This works even when cookies are blocked by Safari ITP
				config.headers["Authorization"] = `Bearer ${token}`;
			}

			// Also send idToken and refreshToken in custom headers
			// The backend will use these for token refresh when cookies are blocked
			if (idToken && config.headers) {
				config.headers["X-ID-Token"] = idToken;
			}
			if (refreshToken && config.headers) {
				config.headers["X-Refresh-Token"] = refreshToken;
			}

			return config;
		},
		(error) => {
			return Promise.reject(error);
		},
	);

	// Response interceptor
	axios.interceptors.response.use(
		(response) => {
			// Validate response data to prevent undefined access errors downstream
			// Some endpoints may return empty or malformed responses
			if (response && response.data === undefined) {
				response.data = {};
			}
			return response;
		},
		(error) => {
			// Handle 401 errors (authentication/token errors)
			if (error.response?.status === 401) {
				const errorMessage = error.response?.data?.message;

				// Silently handle "Invalid Token" errors - user will be logged out
				// by the checkTokenValidity function
				if (
					errorMessage === "Invalid Token" ||
					errorMessage === "Authentication failed"
				) {
					// ── Collect (business) session expired ──────────────────
					// When a BusinessUser's token can no longer be refreshed the
					// backend returns 401 "Invalid Token". Unlike the circle
					// session (handled by `checkTokenValidity`), nothing else
					// signs the business user out — so a hard refresh would leave
					// the Collect bootstrap spinner spinning forever. Detect this
					// case, purge the stale session + any impersonation marker,
					// and send the user to a fresh sign-in so they re-authenticate
					// with new tokens.
					const reqUrl = (error.config?.url || "").toString();
					const isCollectRequest = reqUrl.includes("/collect/");
					// Never redirect off the auth endpoints themselves (a bad
					// login must surface its own error, not bounce the user).
					const isCollectAuthEndpoint = reqUrl.includes("/collect/auth/");
					const alreadyOnSignIn =
						typeof window !== "undefined" &&
						window.location.pathname.startsWith("/collect/sign-in");

					if (
						isCollectRequest &&
						!isCollectAuthEndpoint &&
						!alreadyOnSignIn &&
						hasBusinessSession() &&
						!businessSessionExpiredHandled
					) {
						businessSessionExpiredHandled = true;
						// Purge every trace of the old session so the next
						// sign-in starts from a clean slate with fresh tokens.
						clearBusinessSession();
						try {
							localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
						} catch {
							/* localStorage unavailable — non-fatal */
						}
						// Full navigation (not client-side) guarantees all
						// in-memory Redux state is reset for the fresh sign-in.
						window.location.replace("/collect/sign-in");
					}

					// Return a resolved promise to suppress the error from appearing in console
					return Promise.resolve({
						data: {
							error: AUTH_REQUIRED_ERROR,
							valid: false,
						},
					});
				}
			}

			// Handle 403 errors (banned/deleted accounts)
			if (error.response?.status === 403) {
				const errorData = error.response.data;

				// Collect (Story 5) — trial ended / subscription lapsed. The
				// tenant gate blocks premium endpoints with a stable code. Send
				// the user to the subscribe page and swallow the error so views
				// don't surface a raw 403.
				if (errorData?.code === "SUBSCRIPTION_REQUIRED") {
					navigate("/collect/subscribe", {
						state: {
							access: errorData?.access,
							state: errorData?.state,
						},
					});
					return Promise.resolve({
						data: { error: "SUBSCRIPTION_REQUIRED" },
					});
				}

				// Check if account is banned
				if (
					errorData?.error === "Account Suspended" ||
					errorData?.details?.isBanned
				) {
					// Redirect to account banned page with full details
					// Include all suspension details so the banned page can display
					// payment options without needing another API call
					navigate("/account-banned", {
						state: {
							bannedAt: errorData?.details?.bannedAt,
							reason: errorData?.details?.reason,
							banType: errorData?.details?.banType,
							canSelfResolve: errorData?.details?.canSelfResolve,
							suspensionDetails: errorData?.details?.suspensionDetails,
						},
					});
					// Return a resolved promise to prevent the error from propagating
					// This stops the error from being re-thrown in service files
					return Promise.resolve({ data: { error: "Account Suspended" } });
				}

				// Check if account is deleted
				if (
					errorData?.error === "Account Deleted" ||
					errorData?.details?.isDeleted
				) {
					// Redirect to account deleted page with details
					navigate("/account-deleted", {
						state: {
							deletedAt: errorData?.details?.deletedAt,
							reason: errorData?.details?.reason,
						},
					});
					// Return a resolved promise to prevent the error from propagating
					// This stops the error from being re-thrown in service files
					return Promise.resolve({ data: { error: "Account Deleted" } });
				}
			}

			// For all other errors, just pass them through
			return Promise.reject(error);
		},
	);
};
